import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Typography,
  Grid,
  Button,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  TextField,
  IconButton,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import UploadIcon from "@mui/icons-material/Upload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/Pending";
import RateReviewIcon from "@mui/icons-material/RateReview";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NavbarLoggedInNew from "../components/Navbar-new/NavbarLoggedInNew";
import api from "../api";

const DIFFICULTY_CONFIG = {
  E: { label: "Easy", color: "#22c55e" },
  M: { label: "Medium", color: "#eab308" },
  H: { label: "Hard", color: "#ef4444" },
};

const STATUS_CONFIG = {
  submitted: { label: "Submitted", color: "#3b82f6", icon: PendingIcon },
  reviewed: { label: "Reviewed", color: "#22c55e", icon: CheckCircleIcon },
  needs_revision: { label: "Needs Revision", color: "#f97316", icon: RateReviewIcon },
};

const AssignmentPage = () => {
  const { id, topicId } = useParams();
  const navigate = useNavigate();
  const userProfile = useSelector((state) => state.userProfile);
  const userRole = userProfile?.data?.role;

  const [assignments, setAssignments] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [topicName, setTopicName] = useState("");
  const [uploadingId, setUploadingId] = useState(null);
  const [reviewingId, setReviewingId] = useState(null);
  const [reviewData, setReviewData] = useState({ score: "", feedback: "" });
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchData();
  }, [topicId, userRole]);

  const fetchData = async () => {
    if (!topicId) return;
    setLoading(true);
    try {
      if (userRole === "mentor") {
        const assignRes = await api.get(`api/course/${topicId}/library-assignments/`);
        setAssignments(assignRes.data?.assignments || []);
        setTopicName(assignRes.data?.topic_name || "");

        try {
          const subRes = await api.get(`api/course/${topicId}/mentor/library-assignments/submissions/`);
          setSubmissions(subRes.data?.submissions || []);
        } catch (subErr) {
          console.error("Failed to fetch submissions:", subErr);
          setSubmissions([]);
        }
      } else {
        const res = await api.get(`api/course/${topicId}/mentee/library-assignments/`);
        setAssignments(res.data?.assignments || []);
        setTopicName(res.data?.topic_name || "");
      }
    } catch (err) {
      console.error("Failed to fetch assignments:", err);
      setMessage({ type: "error", text: "Failed to load assignments" });
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (assignmentId) => {
    try {
      const endpoint = userRole === "mentor"
        ? `api/assignment-library/${assignmentId}/download/`
        : `api/course/${topicId}/mentee/library-assignments/${assignmentId}/download/`;
      const res = await api.get(endpoint);
      window.open(res.data.download_url, "_blank");
    } catch (err) {
      setMessage({ type: "error", text: "Failed to get download link" });
    }
  };

  const handleUnassign = async (assignmentId) => {
    try {
      await api.delete(`api/course/${topicId}/library-assignments/assign/`, {
        data: { assignments: [assignmentId] }
      });
      setMessage({ type: "success", text: "Assignment removed from module" });
      fetchData();
    } catch (err) {
      setMessage({ type: "error", text: "Failed to unassign" });
    }
  };

  const handleSubmitAssignment = async (assignmentId, file) => {
    if (!file) return;
    setUploadingId(assignmentId);
    setMessage(null);

    try {
      const presignedRes = await api.post(
        `api/course/${topicId}/mentee/library-assignments/presigned-url/`,
        { filename: file.name, content_type: file.type || "application/zip" }
      );

      const { presigned_url, fields, file_key } = presignedRes.data;

      // Upload to GCS using PUT with signed URL
      await fetch(presigned_url, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": fields["Content-Type"] || file.type || "application/zip",
        },
      });

      await api.post(
        `api/course/${topicId}/mentee/library-assignments/${assignmentId}/submit/`,
        { file_key, file_name: file.name, file_size: file.size }
      );

      setMessage({ type: "success", text: "Assignment submitted successfully!" });
      fetchData();
    } catch (err) {
      setMessage({ type: "error", text: "Failed to submit assignment" });
    } finally {
      setUploadingId(null);
    }
  };

  const handleDownloadSubmission = async (submissionId) => {
    try {
      const res = await api.get(
        `api/course/${topicId}/mentor/library-assignments/submissions/${submissionId}/download/`
      );
      window.open(res.data.download_url, "_blank");
    } catch (err) {
      setMessage({ type: "error", text: "Failed to get download link" });
    }
  };

  const handleReviewSubmission = async (submissionId) => {
    try {
      await api.post(
        `api/course/${topicId}/mentor/library-assignments/submissions/${submissionId}/review/`,
        {
          status: "reviewed",
          score: reviewData.score || null,
          feedback: reviewData.feedback,
        }
      );
      setMessage({ type: "success", text: "Review submitted!" });
      setReviewingId(null);
      setReviewData({ score: "", feedback: "" });
      fetchData();
    } catch (err) {
      setMessage({ type: "error", text: "Failed to submit review" });
    }
  };

  return (
    <>
      <NavbarLoggedInNew />
      <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2 }}>
          <IconButton onClick={() => navigate(`/dashboard/workspace/${id}`)}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, fontFamily: "Be Vietnam Pro" }}>
              Assignments
            </Typography>
            {topicName && (
              <Typography variant="body2" sx={{ color: "#6b7280" }}>
                {topicName}
              </Typography>
            )}
          </Box>
        </Box>

        {message && (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 1,
              backgroundColor: message.type === "success" ? "#dcfce7" : "#fee2e2",
              color: message.type === "success" ? "#166534" : "#991b1b",
            }}
          >
            {message.text}
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : assignments.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <Typography variant="h6" sx={{ fontFamily: "Be Vietnam Pro", color: "#6b7280" }}>
              No assignments assigned to this module yet.
            </Typography>
            {userRole === "mentor" && (
              <Typography variant="body2" sx={{ fontFamily: "Be Vietnam Pro", color: "#9ca3af", mt: 1 }}>
                Go to Assignment Library to assign assignments to this module.
              </Typography>
            )}
          </Box>
        ) : (
          <>
            <Typography variant="h6" sx={{ mb: 2, fontFamily: "Be Vietnam Pro", fontWeight: 600 }}>
              {userRole === "mentor" ? "Assigned Assignments" : "Your Assignments"}
            </Typography>

            <Grid container spacing={2}>
              {assignments.map((a) => (
                <Grid item xs={12} key={a.id}>
                  <Card sx={{ borderRadius: 2, boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
                    <CardContent>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, fontFamily: "Be Vietnam Pro" }}>
                            {a.title}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.5, fontFamily: "Be Vietnam Pro" }}>
                            {a.description}
                          </Typography>
                          <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
                            <Chip
                              label={DIFFICULTY_CONFIG[a.difficulty]?.label || a.difficulty}
                              size="small"
                              sx={{
                                backgroundColor: `${DIFFICULTY_CONFIG[a.difficulty]?.color}20`,
                                color: DIFFICULTY_CONFIG[a.difficulty]?.color,
                                fontWeight: 600,
                              }}
                            />
                            <Chip label={`v${a.current_version}`} size="small" variant="outlined" />
                            {a.estimated_time_hours && (
                              <Chip label={`~${a.estimated_time_hours}h`} size="small" variant="outlined" />
                            )}
                          </Box>
                        </Box>

                        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<DownloadIcon />}
                            onClick={() => handleDownload(a.id)}
                            sx={{ textTransform: "none" }}
                          >
                            Download
                          </Button>

                          {userRole === "mentor" && (
                            <Button
                              variant="outlined"
                              size="small"
                              color="error"
                              startIcon={<DeleteIcon />}
                              onClick={() => handleUnassign(a.id)}
                              sx={{ textTransform: "none" }}
                            >
                              Unassign
                            </Button>
                          )}

                          {userRole === "mentee" && (
                            <Button
                              variant="contained"
                              size="small"
                              component="label"
                              startIcon={uploadingId === a.id ? <CircularProgress size={16} /> : <UploadIcon />}
                              disabled={uploadingId === a.id}
                              sx={{ textTransform: "none", backgroundColor: "#0172A6" }}
                            >
                              {a.submission ? "Resubmit" : "Submit"}
                              <input
                                type="file"
                                hidden
                                accept=".zip,application/zip"
                                onChange={(e) => {
                                  if (e.target.files[0]) {
                                    handleSubmitAssignment(a.id, e.target.files[0]);
                                  }
                                }}
                              />
                            </Button>
                          )}
                        </Box>
                      </Box>

                      {/* Mentee: Show submission status */}
                      {userRole === "mentee" && a.submission && (
                        <Box sx={{ mt: 2, p: 2, backgroundColor: "#f8fafc", borderRadius: 1 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                            {STATUS_CONFIG[a.submission.status] && (
                              <>
                                {React.createElement(STATUS_CONFIG[a.submission.status].icon, {
                                  sx: { color: STATUS_CONFIG[a.submission.status].color, fontSize: 20 },
                                })}
                                <Chip
                                  label={STATUS_CONFIG[a.submission.status].label}
                                  size="small"
                                  sx={{
                                    backgroundColor: `${STATUS_CONFIG[a.submission.status].color}20`,
                                    color: STATUS_CONFIG[a.submission.status].color,
                                    fontWeight: 600,
                                  }}
                                />
                              </>
                            )}
                            {a.submission.score && (
                              <Chip label={`Score: ${a.submission.score}`} size="small" color="primary" />
                            )}
                          </Box>
                          <Typography variant="body2" sx={{ color: "#6b7280" }}>
                            Submitted: {a.submission.file_name}
                          </Typography>
                          {a.submission.feedback && (
                            <Box sx={{ mt: 1, p: 1.5, backgroundColor: "#fff", borderRadius: 1, border: "1px solid #e5e7eb" }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                Mentor Feedback:
                              </Typography>
                              <Typography variant="body2" sx={{ color: "#374151" }}>
                                {a.submission.feedback}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Mentor: Show submissions */}
            {userRole === "mentor" && submissions.length > 0 && (
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" sx={{ mb: 2, fontFamily: "Be Vietnam Pro", fontWeight: 600 }}>
                  Submissions to Review
                </Typography>
                <Grid container spacing={2}>
                  {submissions.map((s) => (
                    <Grid item xs={12} key={s.id}>
                      <Card sx={{ borderRadius: 2, boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
                        <CardContent>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                            <Box>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, fontFamily: "Be Vietnam Pro" }}>
                                {s.assignment_title}
                              </Typography>
                              <Typography variant="body2" sx={{ color: "#6b7280" }}>
                                Submitted by: {s.mentee_name}
                              </Typography>
                              <Typography variant="body2" sx={{ color: "#9ca3af", fontSize: 12 }}>
                                {s.file_name} • {new Date(s.submitted_at).toLocaleDateString()}
                              </Typography>
                              <Box sx={{ mt: 1 }}>
                                {STATUS_CONFIG[s.status] && (
                                  <Chip
                                    label={STATUS_CONFIG[s.status].label}
                                    size="small"
                                    sx={{
                                      backgroundColor: `${STATUS_CONFIG[s.status].color}20`,
                                      color: STATUS_CONFIG[s.status].color,
                                      fontWeight: 600,
                                    }}
                                  />
                                )}
                                {s.score && <Chip label={`Score: ${s.score}`} size="small" sx={{ ml: 1 }} />}
                              </Box>
                            </Box>

                            <Box sx={{ display: "flex", gap: 1 }}>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<DownloadIcon />}
                                onClick={() => handleDownloadSubmission(s.id)}
                                sx={{ textTransform: "none" }}
                              >
                                Download
                              </Button>
                              {s.status === "submitted" && (
                                <Button
                                  variant="contained"
                                  size="small"
                                  startIcon={<RateReviewIcon />}
                                  onClick={() => {
                                    setReviewingId(s.id);
                                    setReviewData({ score: "", feedback: "" });
                                  }}
                                  sx={{ textTransform: "none", backgroundColor: "#0172A6" }}
                                >
                                  Review
                                </Button>
                              )}
                            </Box>
                          </Box>

                          {/* Review form */}
                          {reviewingId === s.id && (
                            <Box sx={{ mt: 2, p: 2, backgroundColor: "#f8fafc", borderRadius: 1 }}>
                              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                Review Submission
                              </Typography>
                              <TextField
                                label="Score (optional)"
                                type="number"
                                size="small"
                                value={reviewData.score}
                                onChange={(e) => setReviewData({ ...reviewData, score: e.target.value })}
                                sx={{ mb: 1, mr: 2, width: 120 }}
                              />
                              <TextField
                                label="Feedback"
                                multiline
                                rows={2}
                                fullWidth
                                size="small"
                                value={reviewData.feedback}
                                onChange={(e) => setReviewData({ ...reviewData, feedback: e.target.value })}
                                sx={{ mb: 1 }}
                              />
                              <Box sx={{ display: "flex", gap: 1 }}>
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={() => handleReviewSubmission(s.id)}
                                  sx={{ textTransform: "none", backgroundColor: "#22c55e" }}
                                >
                                  Submit Review
                                </Button>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => setReviewingId(null)}
                                  sx={{ textTransform: "none" }}
                                >
                                  Cancel
                                </Button>
                              </Box>
                            </Box>
                          )}

                          {/* Show existing feedback */}
                          {s.feedback && s.status !== "submitted" && (
                            <Box sx={{ mt: 2, p: 1.5, backgroundColor: "#f0fdf4", borderRadius: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                Your Feedback:
                              </Typography>
                              <Typography variant="body2">{s.feedback}</Typography>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </>
        )}
      </Box>
    </>
  );
};

export default AssignmentPage;
